/**
 * useParentOrigin
 * ----------------------------------------------------------------------------
 * Reads `?parentOrigin=<https://...>` from the URL once, on first import.
 *
 * - When present (set by the new Shopify Remix theme app extension), the
 *   customizer enters "strict" mode: postMessages are sent ONLY to that origin
 *   and incoming messages from any other origin are ignored.
 *
 * - When absent (legacy: existing Tossware liquid, Magento integration,
 *   standalone admin), the customizer stays in "legacy" mode and behaves
 *   exactly as before — postMessages target '*' and inbound messages are
 *   accepted regardless of origin. A console.warn is emitted once so we can
 *   trace which integrations still need to be migrated.
 *
 * The flag is captured at module load (URL params don't change at runtime).
 */

const PARENT_ORIGIN_PARAM = 'parentOrigin';

function readParentOriginFromUrl(): string | null {
    if (typeof window === 'undefined') return null;
    try {
        const raw = new URLSearchParams(window.location.search).get(PARENT_ORIGIN_PARAM);
        if (!raw) return null;
        // Normalize and validate: must be a same-protocol origin (https://host[:port]).
        const url = new URL(raw);
        if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
        return `${url.protocol}//${url.host}`;
    } catch {
        return null;
    }
}

const allowedParentOrigin = readParentOriginFromUrl();
const strictMode = allowedParentOrigin !== null;

if (typeof window !== 'undefined' && !strictMode && window.parent !== window) {
    // Soft signal — we're in an iframe but the parent didn't pass parentOrigin.
    // Helpful to spot integrations that should be migrated to the Remix bridge.
    // eslint-disable-next-line no-console
    console.warn(
        '[c3d] running in legacy postMessage mode (no ?parentOrigin= in URL). ' +
            'Origin checks are disabled. Migrate the parent integration to pass parentOrigin.'
    );
}

export function useParentOrigin() {
    return {
        /** The origin we are allowed to talk to, or null in legacy mode. */
        allowedParentOrigin,
        /** Whether origin checks are enforced. */
        strictMode,
        /**
         * Returns the postMessage targetOrigin to use for outbound messages:
         *  - strict mode → the captured parent origin
         *  - legacy mode → '*' (current behavior)
         */
        outboundTargetOrigin(): string {
            return strictMode && allowedParentOrigin ? allowedParentOrigin : '*';
        },
        /**
         * In strict mode, returns true only when the inbound event origin
         * matches the captured parent origin. In legacy mode, always returns
         * true (preserves existing behavior).
         */
        isInboundOriginAllowed(eventOrigin: string): boolean {
            if (!strictMode) return true;
            return eventOrigin === allowedParentOrigin;
        }
    };
}
